import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getFirebaseAuth, isFirebaseConfigured } from '../config/firebase';
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import axios from 'axios';
import { getApiBaseUrl } from '../api/client';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [idToken, setIdToken] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [accountsError, setAccountsError] = useState(null); // e.g. 'Could not load teams (check backend/DB)'
  const [selectedAccountId, setSelectedAccountIdState] = useState(() => {
    return localStorage.getItem('monitor_selected_account_id') || null;
  });
  const [accountsLoading, setAccountsLoading] = useState(false);

  const auth = getFirebaseAuth();

  const setSelectedAccountId = useCallback((id) => {
    setSelectedAccountIdState(id);
    if (id) {
      localStorage.setItem('monitor_selected_account_id', id);
    } else {
      localStorage.removeItem('monitor_selected_account_id');
    }
  }, []);

  // Handle redirect result (e.g. after sign-in in Cursor/embedded browser)
  useEffect(() => {
    if (!auth) return;
    getRedirectResult(auth)
      .then((result) => {
        if (result?.user) {
          setUser(result.user);
          if (result.user) result.user.getIdToken().then(setIdToken);
        }
      })
      .catch(() => {});
  }, [auth]);

  useEffect(() => {
    if (!auth) return;
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (u) {
        u.getIdToken().then(setIdToken);
      } else {
        setIdToken(null);
        setAccounts([]);
        setSelectedAccountId(null);
      }
    });
    return () => unsub();
  }, [auth, setSelectedAccountId]);

  // Refresh idToken periodically
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      user.getIdToken(true).then(setIdToken);
    }, 55 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    if (!user || !idToken) {
      setAccounts([]);
      return;
    }
    setAccountsLoading(true);
    setAccountsError(null);
    const base = getApiBaseUrl();
    const url = base ? `${base}/api/accounts` : '/api/accounts';
    axios
      .get(url, {
        headers: { Authorization: `Bearer ${idToken}` },
        timeout: 10000,
      })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setAccounts(list);
        setAccountsError(list.length === 0 ? 'No team in DB for this user. Run Cloud SQL proxy if DB is remote.' : null);
        const current = localStorage.getItem('monitor_selected_account_id');
        const currentInList = list.some((a) => a.id === current);
        if (!currentInList && current) {
          setSelectedAccountId(null);
        } else if (!current && list.length > 0) {
          const frasyn = list.find((a) => (a.name || '').toLowerCase() === 'frasyndicate');
          const id = frasyn ? frasyn.id : list[0].id;
          setSelectedAccountId(id);
        }
      })
      .catch((err) => {
        setAccounts([]);
        const msg = err?.response?.status === 503 ? 'Backend cannot reach DB. Run Cloud SQL proxy (see scripts/connect-cloud-sql-local.sh).' : 'Could not load teams (check backend/DB).';
        setAccountsError(msg);
      })
      .finally(() => {
        setAccountsLoading(false);
      });
  }, [user, idToken, setSelectedAccountId]);

  const login = useCallback(async () => {
    if (!auth) return;
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    const useRedirect =
      process.env.REACT_APP_USE_REDIRECT_SIGNIN === 'true' ||
      process.env.REACT_APP_USE_REDIRECT_SIGNIN === '1';
    if (useRedirect) {
      await signInWithRedirect(auth, provider);
      return;
    }
    try {
      await signInWithPopup(auth, provider);
    } catch (err) {
      const code = err?.code || '';
      const isPopupBlocked =
        code === 'auth/popup-blocked' ||
        code === 'auth/cancelled-popup-request' ||
        (typeof err?.message === 'string' && err.message.toLowerCase().includes('popup'));
      if (isPopupBlocked || code === 'auth/popup-closed-by-user') {
        await signInWithRedirect(auth, provider);
      } else {
        throw err;
      }
    }
  }, [auth]);

  const logout = useCallback(async () => {
    if (!auth) return;
    await firebaseSignOut(auth);
  }, [auth]);

  const getAuthHeaders = useCallback(() => {
    const headers = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (selectedAccountId) headers['X-Account-Id'] = selectedAccountId;
    return headers;
  }, [idToken, selectedAccountId]);

  const value = {
    isFirebaseConfigured: isFirebaseConfigured(),
    user,
    idToken,
    login,
    logout,
    accounts,
    accountsLoading,
    accountsError,
    selectedAccountId,
    setSelectedAccountId,
    getAuthHeaders,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  return ctx;
}
